import {
  EmptyObject,
  ZodShape,
  Option,
  ArrayItem2,
  FlattenTypedValue,
  NonNeverKeys,
  SkipNever,
  Mapper,
} from '@client/new/helpers/types';
import { ZodObject } from 'zod';
import { IntroSectionSpec, Introduction } from './sections/introduction';
import { OfferSectionSpec, Offer } from './sections/offer';

export type LandingPageReadModel<T extends SectionName> = {
  sections: Array<DomainSection<T>>;
  sectionData: SectionsDataMap<T>;
};
export type LandingPageWriteModel<T extends SectionName> = {
  save: (vs: Array<DomainSection<T>>) => Promise<any>;
};

export type LandingPageModel<T extends SectionName> = LandingPageReadModel<T> &
  LandingPageWriteModel<T>;

export type SectionsDataMap<T extends SectionName = SectionName> = SkipNever<{
  [K in T]: SectionsSpecMap[K]['data'];
}> & { base: DataFields<SectionBase, 'bgColor'> };

export type SectionSpec<
  TServer extends EmptyObject = any,
  TDomain extends SectionBase = any,
  TData extends SectionData<TDomain> = never,
> = {
  server: TServer;
  domain: TDomain;
  data: TData;
};

export type SectionDataKeys<T extends SectionName> = NonNeverKeys<
  SectionsSpecMap[T]['data']
>;

export type SectionFieldData<T, K extends keyof T> = {
  options: Option<ArrayItem2<T[K]>>[];
  fallbackValue?: T[K];
};

export type DataFields<T, K extends keyof T> = {
  [P in K]: SectionFieldData<T, P>;
};

export type SectionData<T> = {
  [K in keyof T]?: SectionFieldData<T, K>;
};

export type DomainSection<T extends SectionName> = FlattenTypedValue<
  T,
  SectionsSpecMap[T]['domain']
>;
export type ServerSection<T extends SectionName> = FlattenTypedValue<
  T,
  SectionsSpecMap[T]['server']
>;
export type AnyDomainSection = {
  [K in SectionName]: FlattenTypedValue<K, SectionsSpecMap[K]['domain']>;
}[SectionName];

export type AnyServerSection = {
  [K in SectionName]: FlattenTypedValue<K, SectionsSpecMap[K]['server']>;
}[SectionName];

export type SectionSchema<T extends SectionName> = ZodObject<
  ZodShape<SectionsSpecMap[T]['domain']>,
  'strip'
>;

export type SectionsValidationMap = {
  [K in SectionName]: ZodShape<SectionsSpecMap[K]['domain']>;
};

export type SectionRenderer<T extends SectionName> = (p: {
  section: SectionsSpecMap[T]['domain'];
  data: SectionsSpecMap[T]['data'];
}) => JSX.Element;

type SectionsSettings = {
  primaryColor: string;
  secondaryColor: string;
  tertiaryColor: string; // #fff
};

export type SectionColor = keyof SectionsSettings;

export type SectionBase = {
  id: string;
  bgColor: SectionColor;
  enabled: boolean;
};

export type CTA = {
  enabled: boolean;
  text: string;
  link: string;
};

export type ServerCTA = { link?: string; text?: string; enabled?: boolean };

export type SectionsSpecMap = {
  Introduction: IntroSectionSpec;
  Offer: OfferSectionSpec;
};

export type SectionName = keyof SectionsSpecMap;

export type SectionConstructor<T extends SectionName> = {
  toDomain: Mapper<SectionsSpecMap[T]['server'], SectionsSpecMap[T]['domain']>;
  toServer: Mapper<SectionsSpecMap[T]['domain'], SectionsSpecMap[T]['server']>;
  schema: SectionSchema<T>;
} & (T extends keyof SectionsDataMap
  ? { toData: (v: any) => SectionsSpecMap[T]['data'] }
  : // ? { toData: <T2>(v: T2) => SectionsDataMap<T> }
    EmptyObject);

export const sectionsConstructorMap: {
  [K in SectionName]: SectionConstructor<K>;
} = { Introduction, Offer };
